import React, { FC } from 'react';
import { observer } from 'mobx-react-lite';
import { makeStyles } from '@material-ui/styles';
import { Box, Button, Grid, Theme, Typography } from '@material-ui/core';
import { RetailPhoneFormat, RetailTextField } from 'components/retail/structures';
import commonStores from 'stores/common';
import { ProgressSpinner } from 'components/common/atoms';
import { ServerError } from 'components/common/structures';
import { useMediaQueries } from 'hooks';
import useI18n from "evcharging/hooks/useI18n";

export const basicFormStyles = makeStyles((theme: Theme) => ({
  card: {
    border: `1px solid ${theme.palette.customColors.gray}`,
    padding: theme.spacing(3, 6),
    backgroundColor: theme.palette.common.white,
    boxShadow: 'none',
    borderRadius: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(10),
    },
  },
  submit: {
    color: 'white',
    width: '100%',
    height: 50,
    padding: '15px 0 15px 0',
    borderRadius: 6,
    backgroundColor: theme.palette.primary.main,
    fontSize: 16,
    fontWeight: 700,
    boxShadow: 'none',
    transition: '150ms linear all',
    '&:hover': {
      opacity: 0.85,
      backgroundColor: theme.palette.primary.main,
      boxShadow: '0 10px 20px 0 rgba(0, 0, 0, 0.05)',
    },
    marginTop: theme.spacing(8),
  },
  icon: {
    marginLeft: 7,
  },
  label: {
    fontSize: 12,
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    '& strong': {
      color: theme.palette.error.main,
    },
  },
}));

const ScheduleForm: FC = observer(() => {
  const {
    scheduleFormStore: {
      onChange,
      onSubmit,
      getForm,
      getErrors,
      data,
      getErrorMessage,
      closeError,
      disableConfirm,
      changeZipCode,
    },
  } = commonStores;
  const { t } = useI18n();
  const classes = basicFormStyles();
  const values = getForm;
  const errors = getErrors;

  const isLargeUp = useMediaQueries((theme: Theme) => theme.breakpoints.up('lg'));

  return (
    <Box className={classes.card}>
      <Grid container spacing={isLargeUp ? 4 : 3}>
        <Grid item lg={6} md={12} sm={6} xs={12}>
          <>
            <Typography className={classes.label}>
              {t('form.label.firstName')} <strong>*</strong>
            </Typography>
            <RetailTextField
              type={'text'}
              id={'first-name'}
              placeholder={t('form.label.message-placeholder')}
              onChange={onChange('firstName')}
              value={values.firstName}
              error={!!errors.firstName}
              helperText={errors.firstName}
              newEnergy5
            />
          </>
        </Grid>
        <Grid item lg={6} md={12} sm={6} xs={12}>
          <>
            <Typography className={classes.label}>
              {t('form.label.lastName')} <strong>*</strong>
            </Typography>
            <RetailTextField
              type={'text'}
              id={'last-name'}
              placeholder={t('form.label.message-placeholder')}
              onChange={onChange('lastName')}
              value={values.lastName}
              error={!!errors.lastName}
              helperText={errors.lastName}
              newEnergy5
            />
          </>
        </Grid>
        <Grid item xs={12}>
          <>
            <Typography className={classes.label}>
              {t('form.label.workEmail')} <strong>*</strong>
            </Typography>
            <RetailTextField
              type={'email'}
              id={'email'}
              placeholder={t('form.label.message-placeholder')}
              onChange={onChange('email')}
              value={values.email}
              error={!!errors.email}
              helperText={errors.email}
              newEnergy5
            />
          </>
        </Grid>
        <Grid item lg={6} md={12} sm={6} xs={12}>
          <>
            <Typography className={classes.label}>
              {t('form.label.phone')} <strong>*</strong>
            </Typography>
            <RetailTextField
              type="text"
              id={'phone'}
              placeholder={'___-___ - ____'}
              onChange={onChange('phone')}
              InputProps={{ inputComponent: RetailPhoneFormat }}
              value={values.phone}
              error={!!errors.phone}
              helperText={errors.phone}
              newEnergy5
            />
          </>
        </Grid>
        <Grid item lg={6} md={12} sm={6} xs={12}>
          <>
            <Typography className={classes.label}>
              {t('form.label.companyName')} <strong>*</strong>
            </Typography>
            <RetailTextField
              type={'text'}
              id={'company'}
              placeholder={t('form.label.message-placeholder')}
              onChange={onChange('company')}
              value={values.company}
              error={!!errors.company}
              helperText={errors.company}
              newEnergy5
            />
          </>
        </Grid>
        <Grid item lg={6} md={12} sm={6} xs={12}>
          <>
            <Typography className={classes.label}>
              {t('form.label.zipCode')} <strong>*</strong>
            </Typography>
            <RetailTextField
              type={'text'}
              id={'zipCode'}
              placeholder={t('form.label.message-placeholder')}
              onChange={changeZipCode()}
              value={values.zipCode}
              error={!!errors.zipCode}
              helperText={errors.zipCode}
              newEnergy5
            />
          </>
        </Grid>
        <Grid item lg={6} md={12} sm={6} xs={12}>
          <>
            <Typography className={classes.label}>
              {t('\'landing.schedule.form.number-of-employees\'')} <strong>*</strong>
            </Typography>
            <RetailTextField
              type={'text'}
              id={'number-of-employees'}
              placeholder={t('form.label.message-placeholder')}
              onChange={onChange('numberOfEmployees')}
              value={values.numberOfEmployees}
              error={!!errors.numberOfEmployees}
              helperText={errors.numberOfEmployees}
              newEnergy5
            />
          </>
        </Grid>
        <Grid item xs={12}>
          <>
            <Typography className={classes.label}>
              {t('landing.schedule.form.how-many-chargers')} <strong>*</strong>
            </Typography>
            <RetailTextField
              type={'text'}
              id={'plan'}
              placeholder={t('form.label.message-placeholder')}
              onChange={onChange('plan')}
              value={values.plan}
              error={!!errors.plan}
              helperText={errors.plan}
              newEnergy5
            />
          </>
        </Grid>
      </Grid>
      {data.isLoading ? (
        <ProgressSpinner className={classes.submit} />
      ) : (
        <Button
          onClick={onSubmit}
          className={classes.submit}
          id={'button-contactUs'}
          variant={'contained'}
          fullWidth
          color={'primary'}
          aria-label="Submit"
        >
          {t('action.submit')}
        </Button>
      )}
      <ServerError
        onClose={disableConfirm}
        reverseColor
        newStyle
        message={!data.isConfirm && t('landing.schedule.alert')}
      />
      <ServerError onClose={closeError} message={getErrorMessage} />
    </Box>
  );
});

export default ScheduleForm;
